import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Phone, ShieldCheck, Star, Heart } from "lucide-react";
import { Button } from "./ui/button";
import { buildWhatsAppGenericUrl } from "../lib/whatsapp";

const HERO_IMAGE =
  "https://images.pexels.com/photos/20860625/pexels-photo-20860625.jpeg";

export const Hero = () => {
  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 sm:pt-32 lg:pt-40 pb-16 sm:pb-24 lg:pb-32 hero-grid overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Left: Text */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="lg:col-span-7"
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-50 border border-brand-100 text-brand-700 text-xs sm:text-sm font-semibold tracking-wide">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-500 animate-pulse" />
              Home-visit physiotherapy · Trusted by 164+ patients
            </div>

            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-7xl font-semibold tracking-tighter text-slate-900 leading-[1.05]">
              Professional Physiotherapy{" "}
              <span className="bg-gradient-to-r from-brand-400 to-accent-400 bg-clip-text text-transparent">
                at Your Doorstep
              </span>
            </h1>

            <p className="mt-6 text-lg sm:text-xl text-slate-600 leading-relaxed max-w-xl">
              Treat pain naturally. Recover faster. Live better. Personalised
              home-visit care by{" "}
              <span className="text-slate-900 font-medium">
                Dr. Gaurav Kumar PT
              </span>{" "}
              — for back pain, sciatica, sports injury, paralysis & more.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                data-testid="hero-book-btn"
                onClick={() => scrollTo("#booking")}
                className="rounded-full bg-brand-500 hover:bg-brand-600 text-white h-12 px-6 text-base shadow-lg shadow-brand-500/25 hover:-translate-y-0.5 transition-all"
              >
                Book Appointment
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
              <a
                data-testid="hero-whatsapp-btn"
                href={buildWhatsAppGenericUrl()}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full h-12 px-6 text-base font-medium border border-slate-200 bg-white text-slate-800 hover:bg-slate-50 hover:-translate-y-0.5 transition-all"
              >
                <Phone className="w-4 h-4 text-brand-600" />
                WhatsApp Consultation
              </a>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4 text-sm">
              <div className="flex items-center gap-2 text-slate-700">
                <ShieldCheck className="w-5 h-5 text-brand-600" strokeWidth={1.6} />
                Certified Physiotherapist
              </div>
              <div className="flex items-center gap-2 text-slate-700">
                <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
                4.9 / 5 from 500+ reviews
              </div>
              <div className="flex items-center gap-2 text-slate-700">
                <Heart className="w-5 h-5 text-rose-500" strokeWidth={1.6} />
                Personalised care
              </div>
            </div>
          </motion.div>

          {/* Right: Image with floating badges */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut", delay: 0.1 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] sm:aspect-[5/6] rounded-[2rem] overflow-hidden shadow-[0_30px_80px_rgba(74,109,65,0.15)] border border-white">
              <img
                src={HERO_IMAGE}
                alt="Physiotherapist treating a patient at home"
                loading="eager"
                fetchpriority="high"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
            </div>

            {/* Floating badges */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="absolute -left-4 sm:-left-8 top-12 bg-white/90 backdrop-blur-xl border border-slate-100 rounded-2xl px-4 py-3 shadow-[0_12px_40px_rgba(0,0,0,0.08)] animate-float"
            >
              <p className="text-2xl font-display font-semibold text-slate-900">
                164+
              </p>
              <p className="text-xs text-slate-500">Patients Treated</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="absolute -right-2 sm:-right-6 bottom-16 bg-white/90 backdrop-blur-xl border border-slate-100 rounded-2xl px-4 py-3 shadow-[0_12px_40px_rgba(0,0,0,0.08)] animate-float"
              style={{ animationDelay: "1.5s" }}
            >
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-brand-500 animate-pulse" />
                <p className="text-xs text-slate-500">Available today</p>
              </div>
              <p className="text-sm font-semibold text-slate-900 mt-0.5">
                Home Visits
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
